import { newHtml } from 't-lib-ui/helpers';
import { createPostParts, postSiteClass } from 't-lib-postviewer';
import type { Post as FeedPost } from '../schema-copy/feed-schema';
import { formatListTime, stripHtml } from '../utils/formatting';
import { createLockSvg, createPlusSvg, createClockSvg, createShareSvg, createSpinnerSvg } from './svg-helpers';
import { persistentConsent } from './embed-consent';
import { isServingOfflineOnly } from '../connector/post-engine';

export interface PostEntry {
  feedId: string;
  postIndex: number;
  element: HTMLDivElement | null;
  height: number;
  loaded: boolean;
  data: FeedPost | null;
  isRead: boolean;
  // Read state when the view was built. "Keep Unread" only reverts posts read
  // during this session (auto-marks), never posts that were already read before.
  initiallyRead: boolean;
  isStarred: boolean;
  isReadLater: boolean;
  manuallyUnread: boolean;
  keepUnread: boolean;
}

export function createLoadingPlaceholder(height = 100): HTMLDivElement {
  return newHtml('div', { class: 'postItem', 'data-unrendered': 'true' },
    newHtml('div', { style: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: `${height}px` } },
      height > 40 ? createSpinnerSvg() : '')) as HTMLDivElement;
}

// Skeleton row for a not-yet-loaded list entry. Mirrors createListRow's column
// layout so rows don't shift when real content replaces them, and shows a calm
// shimmer instead of an empty gap or a per-row spinner (which would look busy at
// list density). The title bar width is varied per row so a run of placeholders
// doesn't read as a single solid block.
export function createListSkeleton(isSingleFeed = false, index = 0): HTMLDivElement {
  const titleWidths = ['62%', '78%', '45%', '70%', '54%'];
  const children: HTMLElement[] = [newHtml('span', { class: 'skelDot' })];
  if (!isSingleFeed)
    children.push(newHtml('span', { class: 'listRowFeed' }, newHtml('span', { class: 'skelBar' })));
  children.push(newHtml('span', { class: 'listRowContent' },
    newHtml('span', { class: 'skelBar', style: { width: titleWidths[index % titleWidths.length] } })));
  children.push(newHtml('span', { class: 'listRowTime' }, newHtml('span', { class: 'skelBar' })));
  return newHtml('div', { class: 'postItem listMode listSkeleton', 'data-unrendered': 'true' }, children) as HTMLDivElement;
}

/** Header action-button slot: plus button when unsubscribed, invisible spacer when subscribed. */
export function createFollowActionSlot(isSubscribed: boolean): HTMLElement {
  if (isSubscribed)
    return newHtml('div', { class: 'followActionSlot', style: { width: '36px', height: '36px', flexShrink: '0' } });
  return newHtml('button', {
    class: 'actionButton followActionSlot flowFocussable sidewayFocus',
    title: 'Follow', 'data-action': 'follow',
  }, createPlusSvg(16)) as HTMLElement;
}

/** Footer separator slot: '+ Follow' button when unsubscribed, null when subscribed (caller skips it). */
export function createFollowSepSlot(isSubscribed: boolean): HTMLElement | null {
  if (isSubscribed)
    return null;
  return newHtml('button', {
    class: 'separatorButton followSepSlot flowFocussable sidewayFocus',
    title: 'Follow', 'data-action': 'follow',
  }, newHtml('span', { class: 'separatorLabel' }, '+ Follow')) as HTMLElement;
}

export function createListRow(
  entry: PostEntry,
  feedName: string,
  isSingleFeed?: boolean,
  isSavedList?: boolean,
): HTMLDivElement {
  const post = entry.data!;
  const children: HTMLElement[] = [];
  const displayRead = isSavedList ? false : entry.isRead;

  // Read indicator: lock if manually kept unread, dot otherwise (no lock in saved lists)
  if (!isSavedList && (entry.keepUnread || entry.manuallyUnread))
    children.push(newHtml('span', { class: 'listRowLock' }, createLockSvg(12)));
  else
    children.push(newHtml('span', { class: `readDot ${displayRead ? 'read' : 'unread'}` }));

  // Column 1: Feed name (omitted for single feed view)
  if (!isSingleFeed)
    children.push(newHtml('span', { class: 'listRowFeed' }, feedName));

  // Column 2: Title + body preview (Tweet-style posts have no title — just the preview)
  const bodyPreview = stripHtml(post.description, post.title ? Math.max(50, 300 - post.title.length) : 300);
  const contentChildren: HTMLElement[] = [];
  if (post.title)
    contentChildren.push(newHtml('span', { class: 'listRowTitle' }, post.title));
  if (bodyPreview)
    contentChildren.push(newHtml('span', { class: `listRowPreview${post.title ? '' : ' noTitle'}` }, post.title ? ` — ${bodyPreview}` : bodyPreview));
  children.push(newHtml('span', { class: 'listRowContent' }, contentChildren));

  // Column 3: Time/date
  children.push(newHtml('span', { class: 'listRowTime' }, post.pubDate ? formatListTime(post.pubDate) : ''));

  return newHtml('div', {
    class: `postItem listMode flowFocussable${displayRead ? ' read' : ''}`,
    tabindex: '0',
    'data-unrendered': 'true',
    'data-feed-id': entry.feedId,
    'data-post-number': String(entry.postIndex),
  }, children) as HTMLDivElement;
}

export function createPostElement(
  entry: PostEntry,
  feedName: string,
  siteUrl: string | undefined,
  viewMode: 'full' | 'single' | 'list',
  isAutoMarkRead?: boolean,
  isSingleFeed?: boolean,
  isSavedList?: boolean,
  isSubscribed?: boolean,
): HTMLDivElement {
  if (viewMode == 'list')
    return createListRow(entry, feedName, isSingleFeed, isSavedList);
  // 'full' and 'single' render identical post cards; they only differ in
  // whether the HeadlineList column is shown next to the PostView.
  const post = entry.data!;
  const link = post.link?.trim();
  const displayRead = isSavedList ? false : entry.isRead;

  // .postActions
  const actionChildren: HTMLElement[] = [];
  if (isSavedList) {
    actionChildren.push(
      createFollowActionSlot(isSubscribed ?? false),
      newHtml('button', {
        class: `actionButton flowFocussable sidewayFocus${entry.isReadLater ? ' readLater' : ''}`,
        title: 'Read later', 'data-action': 'toggleReadLater',
      }, createClockSvg(18)),
      newHtml('button', {
        class: `actionButton flowFocussable sidewayFocus${entry.isStarred ? ' starred' : ''}`,
        title: 'Star', 'data-action': 'toggleStar',
      }, '★'),
    );
  } else {
    actionChildren.push(
      newHtml('button', {
        class: `actionButton flowFocussable sidewayFocus${isAutoMarkRead && entry.keepUnread ? ' keepActive' : ''}`,
        title: isAutoMarkRead ? 'Keep unread' : (entry.isRead ? 'Mark unread' : 'Mark read'),
        'data-action': 'toggleRead',
      }, [newHtml('span', { class: `readDot ${entry.isRead ? 'read' : 'unread'}` }), createLockSvg(14)]),
      newHtml('button', {
        class: `actionButton flowFocussable sidewayFocus${entry.isReadLater ? ' readLater' : ''}`,
        title: 'Read later', 'data-action': 'toggleReadLater',
      }, createClockSvg(18)),
      newHtml('button', {
        class: `actionButton flowFocussable sidewayFocus${entry.isStarred ? ' starred' : ''}`,
        title: 'Star', 'data-action': 'toggleStar',
      }, '★'),
    );
  }

  // Meta line, title, byline, media and body come from t-lib-postviewer; only the actions are the reader's.
  // Newsletter feeds store mailto: in siteUrl, which createPostParts doesn't link.
  const articleChildren: HTMLElement[] = createPostParts(post, {
    feedName, feedHref: siteUrl, consent: persistentConsent,
    headerActions: newHtml('div', { class: 'postActions' }, actionChildren),
    offline: isServingOfflineOnly(),
  });

  // .cardActionsRow
  const sepBtn = (cls: string, attrs: any, children: any) =>
    newHtml('button', { class: `separatorButton flowFocussable sidewayFocus${cls}`, ...attrs }, children);
  const sepLabel = (text: string) => newHtml('span', { class: 'separatorLabel' }, text);

  const secondAction = isSavedList
    ? createFollowSepSlot(isSubscribed ?? false)
    : (() => {
        const readIcon = isAutoMarkRead
          ? (() => { const l = createLockSvg(14); l.setAttribute('class', 'separatorIcon'); return l; })()
          : newHtml('span', { class: `readDot ${entry.isRead ? 'read' : 'unread'}` });
        return sepBtn(`${isAutoMarkRead ? ' keepMode' : ''}${isAutoMarkRead && entry.keepUnread ? ' keepActive' : ''}`,
          { title: isAutoMarkRead ? 'Keep unread' : (entry.isRead ? 'Mark as unread' : 'Mark as read'), 'data-action': 'toggleRead' },
          [readIcon, sepLabel(isAutoMarkRead ? (entry.keepUnread ? 'Kept' : 'Keep') : (entry.isRead ? 'Read' : 'Unread'))]);
      })();

  const sepChildren: (HTMLElement | null)[] = [
    link ? newHtml('a', { href: link, target: '_blank', rel: 'noopener noreferrer', class: 'separatorButton flowFocussable', title: 'View original post' }, [
      newHtml('span', { class: 'separatorIcon' }, '↗'), sepLabel('Original Post'),
    ]) : null,
    secondAction,
    sepBtn(entry.isReadLater ? ' readLater' : '',
      { title: 'Read later', 'data-action': 'toggleReadLater' },
      [createClockSvg(16), sepLabel('Read later')]),
    sepBtn(entry.isStarred ? ' starred' : '',
      { title: entry.isStarred ? 'Unstar' : 'Star', 'data-action': 'toggleStar' },
      [newHtml('span', { class: 'separatorIcon' }, '★'), sepLabel(entry.isStarred ? 'Unstar' : 'Star')]),
    sepBtn('',
      { title: 'Share', 'data-action': 'share' },
      [createShareSvg(14), sepLabel('Share')]),
  ];
  articleChildren.push(newHtml('div', { class: 'cardActionsRow' },
    newHtml('div', { class: 'separatorActions' }, sepChildren.filter(Boolean) as HTMLElement[])));

  // feed-<domain> scopes the site's stylesheet, if it has one (site-css.ts in t-lib-postviewer).
  const siteClass = postSiteClass(siteUrl);
  const article = newHtml('article', {
    class: siteClass ? `postCard ${siteClass}` : 'postCard',
    'data-post-id': `${entry.feedId}-${entry.postIndex}`,
    'data-feed-id': entry.feedId,
    'data-post-number': String(entry.postIndex),
    'data-is-read': String(displayRead),
  }, articleChildren);

  return newHtml('div', {
    class: 'postItem',
    'data-unrendered': 'true',
  }, article) as HTMLDivElement;
}

export function updatePostStatus(el: HTMLDivElement, entry: PostEntry, isAutoMarkRead?: boolean) {
  // Update list row read indicator (dot vs lock)
  if (el.classList.contains('listMode')) {
    const indicator = el.querySelector(':scope > .readDot, :scope > .listRowLock');
    if (indicator) {
      if (entry.keepUnread || entry.manuallyUnread) {
        if (indicator.classList.contains('readDot')) {
          const lock = newHtml('span', { class: 'listRowLock' }, createLockSvg(12));
          indicator.replaceWith(lock);
        }
      } else {
        if (indicator.classList.contains('listRowLock')) {
          const dot = newHtml('span', { class: `readDot ${entry.isRead ? 'read' : 'unread'}` });
          indicator.replaceWith(dot);
        } else {
          indicator.className = `readDot ${entry.isRead ? 'read' : 'unread'}`;
        }
      }
    }
    el.classList.toggle('read', entry.isRead && !entry.keepUnread && !entry.manuallyUnread);
  }

  const article = el.querySelector('article');
  if (!article) return;

  article.dataset.isRead = String(entry.isRead);

  el.querySelectorAll('.readDot').forEach(dot =>
    dot.className = `readDot ${entry.isRead ? 'read' : 'unread'}`
  );

  el.querySelectorAll<HTMLElement>('[data-action="toggleRead"]').forEach(btn => {
    if (isAutoMarkRead) {
      btn.classList.toggle('keepActive', entry.keepUnread);
      btn.title = 'Keep unread';
      if (btn.classList.contains('separatorButton')) {
        const label = btn.querySelector('.separatorLabel');
        if (label) label.textContent = entry.keepUnread ? 'Kept' : 'Keep';
      }
    } else if (btn.classList.contains('separatorButton')) {
      btn.title = entry.isRead ? 'Mark as unread' : 'Mark as read';
      const label = btn.querySelector('.separatorLabel');
      if (label) label.textContent = entry.isRead ? 'Read' : 'Unread';
    } else {
      btn.title = entry.isRead ? 'Mark unread' : 'Mark read';
    }
  });

  el.querySelectorAll<HTMLElement>('[data-action="toggleStar"]').forEach(btn => {
    btn.classList.toggle('starred', entry.isStarred);
    if (btn.classList.contains('separatorButton')) {
      btn.title = entry.isStarred ? 'Unstar' : 'Star';
      const label = btn.querySelector('.separatorLabel');
      if (label) label.textContent = entry.isStarred ? 'Unstar' : 'Star';
    }
  });

  el.querySelectorAll<HTMLElement>('[data-action="toggleReadLater"]').forEach(btn =>
    btn.classList.toggle('readLater', entry.isReadLater)
  );
}
